import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "react-hot-toast";
import { UserCheck, Key, Mail, Lock, CheckCircle2, Copy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

// استخراج رسالة خطأ مقروءة من أي صيغة استجابة (FastAPI/Pydantic/Network)
function extractErrorMessage(err, fallback) {
  // خطأ شبكة بدون استجابة
  if (!err?.response) {
    if (err?.message === "Network Error") return "لا يوجد اتصال بالإنترنت. حاول مرة أخرى";
    return err?.message || fallback;
  }
  const data = err.response.data;
  // detail نصي
  if (typeof data?.detail === "string") return data.detail;
  // detail مصفوفة (Pydantic 422)
  if (Array.isArray(data?.detail)) {
    const first = data.detail[0];
    if (first?.msg) {
      const field = Array.isArray(first.loc) ? first.loc[first.loc.length - 1] : "";
      const fieldAr = field === "email" ? "البريد الإلكتروني" :
                      field === "password" ? "كلمة السر" :
                      field === "invite_code" ? "كود الدعوة" : field;
      // ترجمة بعض رسائل Pydantic الشائعة
      if (first.msg.includes("valid email")) return `البريد الإلكتروني غير صالح. تأكد من احتوائه على @ وصيغة صحيحة`;
      if (first.msg.includes("Field required")) return `الحقل مطلوب: ${fieldAr}`;
      return `${fieldAr}: ${first.msg}`;
    }
  }
  // message عام
  if (typeof data?.message === "string") return data.message;
  return fallback;
}

export default function EmployeeRegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1); // 1: enter code, 2: register
  const [code, setCode] = useState("");
  const [inviteInfo, setInviteInfo] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoVerifying, setAutoVerifying] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState(""); // ✨ البريد المسجّل (للعرض في شاشة النجاح)

  // ✨ Auto-fill من الـ URL: /employee-register?code=NZ-XXXX-XXXX
  useEffect(() => {
    const urlCode = searchParams.get("code");
    if (!urlCode) return;
    const clean = urlCode.trim().toUpperCase();
    setCode(clean);
    // تحقق تلقائي
    setAutoVerifying(true);
    axios.get(`${API}/sub-accounts/invites/verify/${clean}`)
      .then((r) => { setInviteInfo(r.data); setStep(2); })
      .catch((err) => {
        console.error("[Auto-verify]", err);
        toast.error(extractErrorMessage(err, "كود الدعوة في الرابط غير صحيح أو منتهي"));
      })
      .finally(() => setAutoVerifying(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyCode = async () => {
    const c = code.trim().toUpperCase();
    if (!c) return toast.error("أدخل كود الدعوة");
    setLoading(true);
    try {
      const r = await axios.get(`${API}/sub-accounts/invites/verify/${c}`);
      setInviteInfo(r.data);
      setStep(2);
    } catch (err) {
      console.error("[Verify code]", err);
      toast.error(extractErrorMessage(err, "كود غير صحيح"));
    } finally {
      setLoading(false);
    }
  };

  const register = async () => {
    const emailClean = email.trim().toLowerCase();
    if (!emailClean || !password) return toast.error("املأ كل الحقول");
    // تحقق بسيط من صيغة البريد قبل إرساله للسيرفر
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailClean)) {
      return toast.error("البريد الإلكتروني غير صالح. الصيغة: name@example.com");
    }
    if (password !== confirmPassword) return toast.error("كلمة السر غير متطابقة");
    if (password.length < 6) return toast.error("كلمة السر يجب أن تكون 6 أحرف على الأقل");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/sub-accounts/register`, {
        invite_code: code.trim().toUpperCase(),
        email: emailClean,
        password,
      });
      // ✨ بدلاً من تسجيل دخول تلقائي، اعرض شاشة تأكيد ودّية
      setRegisteredEmail(r.data.user?.email || emailClean);
      setStep(3); // success screen
      toast.success("🎉 تم إنشاء حسابك بنجاح!");
    } catch (err) {
      console.error("[Employee register]", err, err?.response?.data);
      toast.error(extractErrorMessage(err, "خطأ في التسجيل. حاول مرة أخرى"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4" dir="rtl">
      <Card className="bg-slate-800 border-slate-700 w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mb-3">
            <UserCheck className="w-8 h-8 text-emerald-400" />
          </div>
          <CardTitle className="text-white text-2xl">تسجيل موظف</CardTitle>
          <p className="text-slate-400 text-sm mt-2">
            {step === 1
              ? (autoVerifying ? "جاري التحقق من الرابط..." : "أدخل كود الدعوة الذي حصلت عليه من مدير المتجر")
              : step === 2
              ? `أهلاً بك في ${inviteInfo?.store_name || "المتجر"}`
              : "تم إنشاء حسابك بنجاح ✓"}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 1 ? (
            <>
              <div className="space-y-2">
                <Label className="text-slate-200 flex items-center gap-1">
                  <Key className="w-4 h-4" /> كود الدعوة
                </Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  className="bg-slate-900 border-slate-700 text-white text-center text-xl font-mono tracking-wider"
                  placeholder="NZ-XXXX-XXXX"
                  data-testid="invite-code-input"
                  autoFocus
                />
              </div>
              <Button
                onClick={verifyCode}
                disabled={loading || !code.trim()}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="verify-code-btn"
              >
                {loading ? "..." : "التحقق من الكود"}
              </Button>
              <div className="text-center text-sm text-slate-500">
                <button onClick={() => navigate("/login")} className="text-emerald-400 hover:underline">
                  لديك حساب؟ تسجيل دخول
                </button>
              </div>
            </>
          ) : step === 2 ? (
            <>
              <div className="bg-emerald-900/20 border border-emerald-700/30 p-3 rounded-lg">
                <div className="text-emerald-300 text-sm">
                  <p>✅ كود الدعوة صحيح</p>
                  <p className="mt-1">الاسم: <span className="text-white font-bold">{inviteInfo.name}</span></p>
                  <p>المنصب: <span className="text-white">{inviteInfo.role}</span></p>
                  <p>المتجر: <span className="text-white">{inviteInfo.store_name}</span></p>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200 flex items-center gap-1">
                  <Mail className="w-4 h-4" /> البريد الإلكتروني
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  placeholder="your@email.com"
                  data-testid="employee-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200 flex items-center gap-1">
                  <Lock className="w-4 h-4" /> كلمة السر
                </Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="employee-password"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-200">تأكيد كلمة السر</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="bg-slate-900 border-slate-700 text-white"
                  data-testid="employee-password-confirm"
                />
              </div>
              <Button
                onClick={register}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                data-testid="register-employee-btn"
              >
                {loading ? "..." : "إنشاء الحساب"}
              </Button>
              <Button
                onClick={() => { setStep(1); setInviteInfo(null); }}
                variant="ghost"
                className="w-full text-slate-400"
              >
                ← العودة
              </Button>
            </>
          ) : (
            // ✨ Step 3: شاشة تأكيد ودّية بعد التسجيل
            <div className="space-y-4" data-testid="employee-register-success">
              <div className="flex flex-col items-center text-center space-y-3 py-2">
                <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center animate-pulse">
                  <CheckCircle2 className="w-12 h-12 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white">تم إنشاء حسابك! 🎉</h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                  مرحباً <span className="text-emerald-300 font-semibold">{inviteInfo?.name}</span>،
                  حسابك جاهز للاستخدام في متجر <span className="text-white font-semibold">{inviteInfo?.store_name}</span>
                </p>
              </div>

              {/* بيانات الدخول - مع تنبيه واضح */}
              <div className="bg-gradient-to-l from-emerald-900/30 to-cyan-900/30 border border-emerald-500/40 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-2 text-amber-300 text-xs font-semibold">
                  ⚠️ احفظ هذه البيانات في مكان آمن — راح تحتاجها كل مرة لتسجيل الدخول
                </div>
                <div className="space-y-2">
                  <div>
                    <Label className="text-slate-400 text-xs">البريد الإلكتروني (اسم المستخدم)</Label>
                    <div className="flex items-center gap-2 bg-slate-900/80 rounded-lg p-2 border border-slate-700">
                      <Mail className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span
                        className="text-white font-mono text-sm flex-1 break-all select-all"
                        data-testid="success-email"
                      >
                        {registeredEmail}
                      </span>
                      <button
                        onClick={() => { navigator.clipboard.writeText(registeredEmail); toast.success("تم نسخ البريد"); }}
                        className="text-cyan-400 hover:text-cyan-300 p-1"
                        title="نسخ"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-slate-400 text-xs">كلمة السر</Label>
                    <div className="flex items-center gap-2 bg-slate-900/80 rounded-lg p-2 border border-slate-700">
                      <Lock className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                      <span className="text-emerald-300 text-sm">
                        كلمة السر التي أنشأتها قبل قليل ✓
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* تنبيهات مهمة */}
              <div className="bg-cyan-900/20 border border-cyan-700/30 rounded-lg p-3 text-xs text-cyan-200 space-y-1">
                <p>💡 ادخل من نفس صفحة <span className="font-bold text-white">"تسجيل الدخول"</span> الرئيسية للتطبيق</p>
                <p>📱 البريد <span className="font-bold">غير حساس لحالة الأحرف</span> — تقدر تكتبه بحروف كبيرة أو صغيرة</p>
                <p>🔒 لو نسيت كلمة السر، تواصل مع صاحب المتجر لإعادة تعيينها</p>
              </div>

              <Button
                onClick={() => navigate("/login")}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-12 text-base font-semibold"
                data-testid="goto-login-btn"
              >
                <UserCheck className="w-5 h-5 me-2" />
                الذهاب إلى صفحة تسجيل الدخول
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
